
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';
import PageSections from '../components/PageSections';

const ArchetypeDetailPage = () => {
    const { id } = useParams();
    const [archetype, setArchetype] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDetail();
    }, [id]);

    const fetchDetail = async () => {
        try {
            setLoading(true);
            const data = await api.getArchetype(id);
            setArchetype(data);
        } catch (err) {
            setError('Archetype not found or failed to load.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="p-10 text-center text-gray-400">Loading details...</div>;
    if (error) return <div className="p-10 text-center text-red-500">{error}</div>;
    if (!archetype) return null;

    const showCoreDesires = (archetype.kinks?.length || 0) >= 4;
    const showBestMatches = (archetype.partners?.length || 0) >= 3;
    const showTypicalActions = (archetype.sexActions?.length || 0) >= 4;

    return (
        <div className="container mx-auto px-4 py-8 max-w-5xl">
            {/* Breadcrumb */}
            <Link to="/archetypes" className="text-gray-500 hover:text-white mb-6 block">
                ← Back to Archetypes
            </Link>

            {/* Parent Variant Breadcrumb */}
            {archetype.parentVariant && (
                <div className="flex items-center gap-2 text-sm text-dark-400 mb-4">
                    <span>Sub-variant of</span>
                    <Link
                        to={`/archetype/${archetype.parentVariant.id}`}
                        className="text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors font-medium"
                    >
                        {archetype.parentVariant.icon && <span className="mr-1">{archetype.parentVariant.icon}</span>}
                        {archetype.parentVariant.name} →
                    </Link>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Left Column: Profile Card */}
                <div className="lg:col-span-1">
                    <div className="bg-[#0A0A0A]/80 border border-wine-800/30 rounded-2xl p-6 sticky top-24">
                        <div className="text-6xl mb-4 text-center">{archetype.icon}</div>
                        <h1 className="text-3xl font-fantasy font-semibold text-center text-cream/95 mb-2 tracking-wide">{archetype.name}</h1>
                        <div className="text-center mb-4">
                            <span className="text-xs uppercase tracking-[0.15em] font-bold text-[#C4A35A]/80 bg-wine-950/50 px-3 py-1 rounded-full border border-wine-700/40">
                                {archetype.category}
                            </span>
                        </div>

                        {/* Gold Ornate Divider */}
                        <div className="ornate-divider w-24 mx-auto mb-4" />

                        <p className="text-cream/60 italic text-center">
                            "{archetype.summary}"
                        </p>
                    </div>
                </div>

                {/* Right Column: Details & Relationships */}
                <div className="lg:col-span-2 space-y-8">

                    {/* Core Desires (Kinks) - Moved to TOP */}
                    {showCoreDesires && (
                        <section>
                            <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-4 tracking-wide">Core Desires</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {archetype.kinks.map(kink => (
                                    <Link key={kink.id} to={`/kink/${kink.id}`} className="block group">
                                        <div className="bg-surface-800 p-4 rounded-xl border border-white/5 hover:border-accent-500/30 transition-all">
                                            <div className="flex justify-between items-start mb-2">
                                                <h4 className="font-bold text-accent-300 group-hover:text-accent-200">{kink.name}</h4>
                                                {kink.ArchetypeKinks?.interest_level && (
                                                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${kink.ArchetypeKinks.interest_level === 'CORE' ? 'bg-accent-500/20 text-accent-300 border-accent-500/30' :
                                                        kink.ArchetypeKinks.interest_level === 'JOY' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                                                            'bg-gray-700 text-gray-400 border-gray-600'
                                                        }`}>
                                                        {kink.ArchetypeKinks.interest_level}
                                                    </span>
                                                )}
                                            </div>
                                            {kink.ArchetypeKinks?.reason && (
                                                <p className="text-xs text-gray-400 border-t border-white/5 pt-2">
                                                    "{kink.ArchetypeKinks.reason}"
                                                </p>
                                            )}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Best Matches (Partners) - Moved to TOP */}
                    {showBestMatches && (
                        <section>
                            <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-4 tracking-wide">Best Matches</h2>
                            <div className="space-y-4">
                                {archetype.partners.map(partner => (
                                    <Link key={partner.id} to={`/archetype/${partner.id}`} className="block group">
                                        <div className="bg-surface-800 p-5 rounded-xl border border-white/5 hover:border-purple-500/30 transition-all flex gap-4 relative overflow-hidden">
                                            <div className="text-3xl relative z-10">{partner.icon}</div>
                                            <div className="relative z-10">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h4 className="font-bold text-purple-300 group-hover:text-purple-200">
                                                        {partner.name}
                                                    </h4>
                                                    {partner.ArchetypePairings?.dynamic_type && (
                                                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${partner.ArchetypePairings.dynamic_type === 'HARMONIOUS' ? 'bg-green-900/30 text-green-400 border-green-500/20' :
                                                            partner.ArchetypePairings.dynamic_type === 'VOLATILE' ? 'bg-red-900/30 text-red-400 border-red-500/20' :
                                                                partner.ArchetypePairings.dynamic_type === 'HIGH-FRICTION' ? 'bg-orange-900/30 text-orange-400 border-orange-500/20' :
                                                                    'bg-blue-900/30 text-blue-400 border-blue-500/20'
                                                            }`}>
                                                            {partner.ArchetypePairings.dynamic_type}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-400">
                                                    {partner.ArchetypePairings?.dynamicDescription}
                                                </p>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Typical Sex Actions */}
                    {showTypicalActions && (
                        <section>
                            <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-4 tracking-wide">Typical Actions</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {archetype.sexActions.map(action => (
                                    <Link key={action.id} to={`/action/${action.id}`} className="block group">
                                        <div className="bg-surface-800 p-4 rounded-xl border border-white/5 hover:border-blue-500/30 transition-all">
                                            <h4 className="font-bold text-blue-300 group-hover:text-blue-200">{action.name}</h4>
                                            <div className="flex justify-between items-center text-sm text-gray-500 mt-1">
                                                <span className="bg-blue-900/30 text-blue-400 px-2 py-0.5 rounded text-xs uppercase tracking-wide">
                                                    {action.ArchetypeSexAction?.frequency}
                                                </span>
                                            </div>
                                            {action.ArchetypeSexAction?.style && (
                                                <p className="text-xs text-gray-400 mt-2 border-t border-white/5 pt-2 italic">
                                                    "{action.ArchetypeSexAction.style}"
                                                </p>
                                            )}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Psychology & Deep Dive - Moved to BOTTOM */}
                    <div className="space-y-6 pt-8 border-t border-wine-700/30">
                        <section className="bg-[#0A0A0A]/80 rounded-2xl p-6 border border-wine-800/30">
                            <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-4 tracking-wide">Psychological Profile</h2>
                            <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-strong:text-cream max-w-none leading-relaxed mb-6">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{archetype.description}</ReactMarkdown>
                            </div>
                            {archetype.psychology && (
                                <div className="bg-blue-900/10 border-l-4 border-blue-500 pl-4 py-2">
                                    <h3 className="text-blue-400 font-bold mb-2 text-sm uppercase tracking-wide">Deep Dive</h3>
                                    <p className="text-gray-300 italic">{archetype.psychology}</p>
                                </div>
                            )}
                        </section>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Shadow Side */}
                            {archetype.shadow_side && (
                                <section className="bg-red-900/10 rounded-2xl p-6 border border-red-500/20">
                                    <h2 className="text-lg font-bold text-red-400 mb-3 flex items-center gap-2">
                                        <span>⚠️</span> The Shadow
                                    </h2>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        {archetype.shadow_side}
                                    </p>
                                </section>
                            )}

                            {/* Communication */}
                            {archetype.communication_style && (
                                <section className="bg-green-900/10 rounded-2xl p-6 border border-green-500/20">
                                    <h2 className="text-lg font-bold text-green-400 mb-3 flex items-center gap-2">
                                        <span>💬</span> Negotiation
                                    </h2>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        {archetype.communication_style}
                                    </p>
                                </section>
                            )}
                        </div>
                    </div>

                    {/* Dynamic Page Sections */}
                    {archetype.pageSections && archetype.pageSections.length > 0 && (
                        <div className="mt-8">
                            <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-4 tracking-wide">Deep Dive</h2>
                            <PageSections
                                pageSections={archetype.pageSections}
                                prioritySections={['overview', 'growth_path']}
                            />
                        </div>
                    )}

                    {/* Sub-Variants */}
                    {archetype.childVariants && archetype.childVariants.length > 0 && (
                        <div className="mt-8">
                            <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-4 tracking-wide flex items-center gap-2">
                                <span>🌿</span> Variants of {archetype.name}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {archetype.childVariants.map(variant => (
                                    <Link key={variant.id} to={`/archetype/${variant.id}`} className="block group">
                                        <div className="bg-surface-800 p-4 rounded-xl border border-white/5 hover:border-accent-500/30 transition-all">
                                            <div className="flex items-center gap-2">
                                                {variant.icon && <span className="text-xl">{variant.icon}</span>}
                                                <h4 className="font-bold text-accent-300 group-hover:text-accent-200">{variant.name}</h4>
                                            </div>
                                            {variant.category && <p className="text-xs text-gray-500 mt-1">{variant.category}</p>}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default ArchetypeDetailPage;
